/**
 * <PackagesDashboardTab /> — replaces the old "All packages dashboard"
 * placeholder with a real intel roll-up.
 *
 * Financial values (MRR, avg margin, brand MRR) are only surfaced when the
 * backend confirms the caller has ``package.view_rates`` / ``package.view_margins``.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EmptyState from "@/components/EmptyState";
import { Package, TrendingUp, Wallet, Clock, AlertTriangle } from "lucide-react";
import { fmtMoney } from "@/pages/app/Packages";

function Stat({ icon: Icon, label, value, tone = "", testid, hint }) {
  return (
    <div className="border border-border p-4 space-y-1" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className={`text-2xl font-bold font-mono leading-tight ${tone}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

const EXPIRY_TONES = {
  expired: "text-red-700 bg-red-500/10 border-red-500/40",
  next_7: "text-amber-700 bg-amber-500/10 border-amber-500/40",
  next_30: "text-amber-700 bg-amber-500/5 border-amber-500/30",
  next_90: "text-sky-700 bg-sky-500/5 border-sky-500/30",
  later: "text-emerald-700 bg-emerald-500/5 border-emerald-500/30",
};

const EXPIRY_LABELS = {
  expired: "Expired",
  next_7: "Next 7 days",
  next_30: "Next 30 days",
  next_90: "Next 90 days",
  later: "Later",
};

export default function PackagesDashboardTab({ wsId }) {
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currency = auth.activeWorkspace?.currency || "INR";
  const ws = wsId || auth.activeWorkspaceId;

  useEffect(() => {
    if (!ws) return;
    let alive = true;
    setLoading(true);
    api.get(`/workspaces/${ws}/packages-dashboard`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ws]);

  if (loading) return <div className="text-sm text-muted-foreground py-6">Loading dashboard…</div>;
  if (!data || data.totals.packages === 0) {
    return <EmptyState title="No packages yet" description="Create your first package to see MRR, hours and expiry rollups." icon={Package} testid="packages-dashboard-empty" />;
  }

  const t = data.totals;
  const cats = Object.entries(data.category_mix || {}).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const maxCat = Math.max(1, ...cats.map(([, c]) => c));

  return (
    <div className="space-y-6" data-testid="packages-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Package} label="Total packages" value={t.packages} testid="stat-packages" />
        <Stat icon={TrendingUp} label="Active" value={t.active} tone="text-emerald-600" testid="stat-active" />
        {data.can_view_rates ? (
          <Stat icon={Wallet} label="MRR" value={fmtMoney(t.mrr, currency)} testid="stat-mrr" hint="Sum of active package net monthly price" />
        ) : (
          <Stat icon={Wallet} label="MRR" value="•••" testid="stat-mrr" hint="Restricted — request package.view_rates" />
        )}
        <Stat icon={Clock} label="Monthly hours" value={`${t.total_hours || 0}`} testid="stat-hours" hint="Delivery capacity locked-in per month" />
        {data.can_view_margins && t.avg_margin_pct !== undefined && (
          <Stat icon={TrendingUp} label="Avg margin" value={`${t.avg_margin_pct}%`} tone={t.avg_margin_pct < 0 ? "text-red-600" : t.avg_margin_pct > 30 ? "text-emerald-600" : ""} testid="stat-margin" />
        )}
        <Stat label="Draft" value={t.draft} testid="stat-draft" />
        <Stat label="Paused" value={t.paused} testid="stat-paused" />
        <Stat label="Completed" value={t.completed} testid="stat-completed" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-border p-6 space-y-3" data-testid="packages-category-mix">
          <div className="label-eyebrow">Categories</div>
          <div className="space-y-1.5">
            {cats.map(([name, count]) => (
              <div key={name} className="flex items-center gap-2">
                <span className="text-xs capitalize truncate flex-1">{name}</span>
                <div className="w-24 h-1.5 bg-secondary">
                  <div className="h-full bg-primary" style={{ width: `${(count * 100) / maxCat}%` }} />
                </div>
                <span className="text-xs font-mono w-6 text-right">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border p-6 space-y-3" data-testid="packages-expiry">
          <div className="label-eyebrow flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> Expiry runway</div>
          <div className="grid grid-cols-5 gap-2">
            {["expired", "next_7", "next_30", "next_90", "later"].map((b) => (
              <div key={b} className={`p-3 border text-center ${EXPIRY_TONES[b]}`}>
                <div className="text-lg font-mono font-bold">{data.expiry_buckets?.[b] || 0}</div>
                <div className="text-[10px] uppercase tracking-wider mt-0.5">{EXPIRY_LABELS[b]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-border p-6" data-testid="packages-top-brands">
        <div className="label-eyebrow mb-3">
          Top brands by {data.can_view_rates ? "MRR" : "active packages"}
        </div>
        <div className="space-y-2">
          {(data.top_brands_by_mrr || []).map((r) => (
            <Link key={r.brand_id} to={`/app/brands/${r.brand_id}`} className="flex items-center justify-between border border-border/60 hover:border-primary/50 p-3 transition group" data-testid={`pkg-top-brand-${r.brand_id}`}>
              <span className="font-medium truncate group-hover:text-primary transition">{r.brand_name}</span>
              <span className="flex items-center gap-4 text-xs shrink-0">
                <span><span className="font-mono font-bold">{r.active_packages}</span> pkg</span>
                {r.mrr != null && <span className="font-mono font-bold min-w-[80px] text-right">{fmtMoney(r.mrr, currency)}</span>}
              </span>
            </Link>
          ))}
          {(!data.top_brands_by_mrr || data.top_brands_by_mrr.length === 0) && (
            <div className="text-xs text-muted-foreground py-2">No active packages yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
